import React, { useCallback,  useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, LoadingSpinner } from '@/components';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useResidentList } from '@/hooks/admin/useResidentList';
import { ResidentDataTable } from '@/components';
import { IResidentTableRow } from '@/types/admin/residentList';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';
import { toResidentCsvRows, RESIDENT_CSV_COLUMNS } from '@/utils/admin/residentsList/csv';
import { ResidentDetailsSidePanel } from '@/components/admin/resident/desktop/ResidentDetailsSidePanel';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { DEFAULT_RESIDENT_FILTER_MODEL, DEFAULT_RESIDENT_SORT_MODEL, DEFAULT_PAGINATION_MODEL } from '@/constants/admin/residents';


type ResidentListProps = StackScreenProps<AdminStackParamList, 'ResidentList'>;

const CSV_COLUMNS = RESIDENT_CSV_COLUMNS;

export const ResidentList: React.FC<ResidentListProps> = ({
	navigation,
}) => {
	const [csvData, setCsvData] = useState<(string | number | boolean | null)[][]>([CSV_COLUMNS]);
	const { showApiError } = useErrorToast();
	const { layoutMode } = useResponsive();

	// Desktop side panel state
	const [detailsPanelVisible, setDetailsPanelVisible] = useState(false);
	const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
	const [selectedLeaseId, setSelectedLeaseId] = useState<number | null>(null);

	const {
		data,
		tableRows,
		isLoading,
		enhancedError,
		retryCount,
		canRetry,
		manualRetry,
		paginationModel,
		filterModel,
		searchQuery,
	} = useResidentList();

	// Show toast for API errors (guard duplicates)
	useErrorToastOnce(enhancedError, showApiError);
	// Keep UI immediate; debounce handled inside hook effects

	// Format CSV data using shared util
	const formatCsvData = useCallback((newData: IResidentTableRow[]) => {
		if (!newData || newData.length === 0) {
			setCsvData([CSV_COLUMNS]);
			return;
		}
		setCsvData(toResidentCsvRows(newData));
	}, []);

	// Handle resident selection
	const selectResidentDetails = useCallback((resident: IResidentTableRow) => {
		if (layoutMode === 'desktop') {
			setSelectedAccountId(resident.accountId);
			setSelectedLeaseId(resident.id);
			setDetailsPanelVisible(true);
			return;
		}
		navigation.navigate('ResidentDetails', { 
			accountId: resident.accountId, 
			leaseId: resident.id // Using resident.id as leaseId
		});
	}, [navigation, layoutMode]);

	// Handle download functionality
	const handleDownload = useCallback(() => {
		if (Platform.OS === 'web') {
			const filename = `resio-residents-${new Date().toJSON().slice(0, 10)}`;
			try {
				downloadCsvFrom2DArray(toResidentCsvRows(tableRows), filename);
			} catch (e) {
				console.error('Failed to download CSV', e);
			}
		} else {
			// Non-web platforms: could share file or show a toast; for now, no-op
			console.log('CSV export is available on web only.');
		}
	}, [tableRows]);

	// Determine if we should show data or empty state
	// Show data if we have data, regardless of loading state (prevents blinking)
	const hasData = data.count > 0;
	
	// Show error state if we have a critical error and no data and no search/filter applied
	const hasSearchOrFilter = searchQuery.trim() !== '' || filterModel.items.length > 0;
	const showErrorState = enhancedError && !hasData && !isLoading && !hasSearchOrFilter;
	
	// Show loading state if we're loading and have no data yet and no search/filter applied
	const showLoadingState = isLoading && !hasData && !showErrorState && !hasSearchOrFilter;
	
	// Always show the data table if there's a search/filter applied, even with no results
	const showDataTable = hasData || hasSearchOrFilter;

	// Render error state component
	const renderErrorState = () => (
		<AdminErrorState
			title="Unable to Load Residents"
			message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the resident list.'}
			retryCount={retryCount}
			canRetry={canRetry}
			onRetry={manualRetry}
		/>
	);

	// Unified DataTableProvider for both desktop and mobile layouts
	return (
		<DataTableProvider 
			tableKey="admin:residents" 
			defaults={{ 
				filterModel: DEFAULT_RESIDENT_FILTER_MODEL,
				sortModel: DEFAULT_RESIDENT_SORT_MODEL as any,
				paginationModel: paginationModel || DEFAULT_PAGINATION_MODEL,
			}}
		>
			{layoutMode === 'desktop' ? (
				<AdminErrorBoundary context="ResidentList">
					<DesktopAdminLayout>
						<DesktopPageHeader
							title="Residents"
							subtitle=""
							showExportButton={true}
							onExportPress={handleDownload}
							exportButtonText="Export Table"
						/>
						{showLoadingState ? (
							<LoadingSpinner message="Loading residents..." />
						) : showErrorState ? (
							renderErrorState()
						) : (
							<View style={styles.desktopDataContainer}>
								<ResidentDataTable
									data={data}
									rows={tableRows}
									isLoading={isLoading}
									onResidentSelect={selectResidentDetails}
									updateCsvData={formatCsvData}
									showSearch={false}
									showSort={true}
								/>
								<ResidentDetailsSidePanel
									visible={detailsPanelVisible}
									onClose={() => setDetailsPanelVisible(false)}
									accountId={selectedAccountId}
									leaseId={selectedLeaseId}
								/>
							</View>
						)}
					</DesktopAdminLayout>
				</AdminErrorBoundary>
			) : (
				<AdminErrorBoundary context="ResidentList">
					<BaseAdminScreen
						screenName="ResidentList"
						navigation={navigation}
						showPlaceholder={false}
						scrollEnabled={false}
						contentContainerStyle={styles.baseScreenContent}
					>
						<AdminEmptyScreenLayout
							title="Residents"
							emptyStateType="residents"
							onAddPress={handleDownload}
							showAddButton={true}
							buttonType="download"
							hasContent={showDataTable || showErrorState || showLoadingState}
						>
							{showLoadingState ? (
								<LoadingSpinner message="Loading residents..." />
							) : showErrorState ? (
								renderErrorState()
							) : showDataTable ? (
								<View style={styles.dataContainer}>
									<ResidentDataTable
										data={data}
										rows={tableRows}
										isLoading={isLoading}
										onResidentSelect={selectResidentDetails}
										updateCsvData={formatCsvData}
										showSearch={false}
									/>
								</View>
							) : null}
						</AdminEmptyScreenLayout>
					</BaseAdminScreen>
				</AdminErrorBoundary>
			)}
		</DataTableProvider>
	);
};

const styles = StyleSheet.create({
	baseScreenContent: {
		paddingHorizontal: 0,
		paddingTop: 0,
		paddingBottom: 0,
		flexGrow: 1,
	},
	dataContainer: {
		flex: 1,
		width: '100%',
	},
	desktopDataContainer: {
		flex: 1,
		width: '100%',
		backgroundColor: 'transparent',
	},
});
